import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Search, Star, X } from "lucide-react";

/* ----------------------- Feedback view ----------------------- */

const FEEDBACK_CATEGORIES = [
    "All", "General", "Burnout check-in", "Behavior tracker", "Appointment prep",
    "Resources", "AI chat (Lane)", "Bug report", "Idea / wish",
];

export default function FeedbackView() {
    const [data, setData] = useState({ items: [], counts: { total: 0, avg_rating: 0 } });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const [minRating, setMinRating] = useState(0);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let active = true;
        const fetchData = async () => {
            try {
                const r = await api.get("/admin/feedback");
                if (active) setData(r.data);
            } catch {
                if (active) toast.error("Couldn't load feedback.");
            } finally {
                if (active) setLoading(false);
            }
        };
        fetchData();
        return () => { active = false; };
    }, [refreshTick]);

    const doDelete = async (id) => {
        setDeletingId(id);
        try {
            await api.delete(`/admin/feedback/${id}`);
            toast.success("Feedback deleted.");
            setConfirmDeleteId(null);
            setRefreshTick(t => t + 1);
        } catch {
            toast.error("Couldn't delete. Try again.");
        } finally {
            setDeletingId(null);
        }
    };

    const bulkDeleteNoStars = async () => {
        const targets = data.items.filter(i => !i.rating || i.rating === 0);
        if (!targets.length) {
            toast.info("No no-star responses to delete.");
            return;
        }
        if (!window.confirm(`Delete ${targets.length} no-star feedback ${targets.length === 1 ? "response" : "responses"}? This can't be undone.`)) return;
        let ok = 0;
        let failed = 0;
        for (const t of targets) {
            try { await api.delete(`/admin/feedback/${t.id}`); ok++; }
            catch { failed++; }
        }
        toast.success(`Deleted ${ok}${failed ? ` (${failed} failed)` : ""}.`);
        setRefreshTick(t => t + 1);
    };

    const items = data.items.filter(it => {
        if (cat !== "All" && (it.category || "") !== cat) return false;
        if (minRating > 0 && (it.rating || 0) < minRating) return false;
        if (q) {
            const needle = q.toLowerCase();
            const hay = `${it.message || ""} ${it.email || ""} ${it.category || ""}`.toLowerCase();
            if (!hay.includes(needle)) return false;
        }
        return true;
    });

    const dist = [1, 2, 3, 4, 5].map(n => data.items.filter(i => i.rating === n).length);

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight">Feedback</h1>
                    <p className="mt-2 text-[#4C5551]">What caregivers are saying about Memory Lane.</p>
                </div>
                {data.items.some(i => !i.rating || i.rating === 0) && (
                    <button
                        onClick={bulkDeleteNoStars}
                        data-testid="admin-feedback-bulk-delete-no-stars"
                        className="inline-flex items-center gap-1.5 bg-white border border-coral text-coral-dark hover:bg-coral-light/40 rounded-2xl px-4 py-2 text-sm font-medium"
                    >
                        <X size={14} strokeWidth={2} /> Delete all no-star responses
                    </button>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5" data-testid="admin-feedback-stats">
                <div className="ml-card p-5 bg-teal-soft !border-transparent">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Total responses</p>
                    <p className="text-3xl font-heading font-semibold text-teal-dark mt-1">{data.counts.total}</p>
                </div>
                <div className="ml-card p-5 bg-yellow-soft !border-transparent">
                    <p className="text-xs uppercase tracking-wider font-semibold text-yellow-dark">Average rating</p>
                    <div className="flex items-baseline gap-2 mt-1">
                        <p className="text-3xl font-heading font-semibold text-yellow-dark">{data.counts.avg_rating || "—"}</p>
                        <Star size={20} strokeWidth={1.6} className="fill-[#E6B400] text-[#E6B400]" />
                    </div>
                </div>
                <div className="ml-card p-5 bg-ml_blue-light !border-transparent">
                    <p className="text-xs uppercase tracking-wider font-semibold text-ml_blue-dark">Rating breakdown</p>
                    <div className="mt-2 space-y-1">
                        {[5, 4, 3, 2, 1].map(n => {
                            const count = dist[n - 1];
                            const pct = data.counts.total ? Math.round((count / data.counts.total) * 100) : 0;
                            return (
                                <div key={n} className="flex items-center gap-2 text-xs text-ml_blue-dark">
                                    <span className="w-3 font-medium">{n}</span>
                                    <Star size={10} strokeWidth={2} className="fill-[#E6B400] text-[#E6B400] shrink-0" />
                                    <div className="flex-1 h-1.5 bg-white/60 rounded-full overflow-hidden">
                                        <div className="h-full bg-ml_blue-dark/70" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="w-6 text-right tabular-nums">{count}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="mt-7 flex flex-col gap-3" data-testid="admin-feedback-filters">
                <div className="flex flex-wrap gap-2">
                    {FEEDBACK_CATEGORIES.map(c => (
                        <button key={c} onClick={() => setCat(c)}
                            data-testid={`admin-feedback-cat-${c.replace(/[^a-z]/gi, '-').toLowerCase()}`}
                            className={`px-3.5 py-1.5 rounded-2xl text-xs font-medium border transition-colors ${cat === c
                                ? "bg-teal text-white border-teal"
                                : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                }`}>{c}</button>
                    ))}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Min rating:</span>
                        {[0, 1, 2, 3, 4, 5].map(n => (
                            <button key={n} onClick={() => setMinRating(n)}
                                data-testid={`admin-feedback-min-${n}`}
                                className={`min-w-[36px] h-9 px-2 rounded-full text-xs font-medium border ${minRating === n
                                    ? "bg-yellow-soft text-yellow-dark border-yellow-soft"
                                    : "bg-white text-[#4C5551] border-[#E2E8F0] hover:bg-cream-dark"
                                    }`}>{n === 0 ? "Any" : `${n}+`}</button>
                        ))}
                    </div>
                    <div className="relative">
                        <Search size={16} strokeWidth={1.6} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7A74]" />
                        <input type="search" value={q} onChange={e => setQ(e.target.value)}
                            data-testid="admin-feedback-search"
                            placeholder="Search message, email…"
                            className="ml-input !min-h-[40px] !py-2 !pl-9 !pr-3 text-sm w-72" />
                    </div>
                </div>
            </div>

            {/* List */}
            <section className="mt-6 space-y-3" data-testid="admin-feedback-list">
                {loading && <p className="text-[#4C5551]">Loading…</p>}
                {!loading && items.length === 0 && (
                    <p className="text-[#4C5551] py-8 text-center bg-white rounded-2xl">No feedback matches these filters yet.</p>
                )}
                {items.map(it => (
                    <article key={it.id} className="ml-card p-5" data-testid={`admin-feedback-item-${it.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <div className="flex" aria-label={`${it.rating} of 5 stars`}>
                                    {[1, 2, 3, 4, 5].map(n => (
                                        <Star key={n} size={16} strokeWidth={1.6}
                                            className={n <= (it.rating || 0)
                                                ? "fill-[#E6B400] text-[#E6B400]"
                                                : "text-[#E2E8F0]"} />
                                    ))}
                                </div>
                                {!it.rating && (
                                    <span className="text-xs px-2 py-0.5 rounded-2xl bg-cream-dark text-[#6E7A74] font-medium italic">
                                        no rating
                                    </span>
                                )}
                                {it.category && (
                                    <span className="text-xs px-2.5 py-0.5 rounded-2xl bg-cream-dark text-[#4C5551] font-medium">
                                        {it.category}
                                    </span>
                                )}
                                {it.participant_id && (
                                    <span
                                        data-testid={`admin-feedback-participant-${it.id}`}
                                        title="User Interviews participant id"
                                        className="text-xs px-2.5 py-0.5 rounded-2xl bg-stage_late-light text-stage_late-dark font-mono"
                                    >
                                        iid: {it.participant_id}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                <p className="text-xs text-[#6E7A74]">
                                    {new Date(it.created_at).toLocaleString()}
                                </p>
                                {confirmDeleteId === it.id ? (
                                    <span className="inline-flex items-center gap-1">
                                        <button
                                            onClick={() => doDelete(it.id)}
                                            disabled={deletingId === it.id}
                                            data-testid={`admin-feedback-delete-confirm-${it.id}`}
                                            className="text-xs font-medium text-white bg-coral-dark hover:opacity-90 rounded-2xl px-3 py-1 disabled:opacity-60"
                                        >
                                            {deletingId === it.id ? "Deleting…" : "Confirm delete"}
                                        </button>
                                        <button
                                            onClick={() => setConfirmDeleteId(null)}
                                            data-testid={`admin-feedback-delete-cancel-${it.id}`}
                                            className="text-xs font-medium text-[#4C5551] hover:bg-cream-dark rounded-2xl px-3 py-1"
                                        >
                                            Cancel
                                        </button>
                                    </span>
                                ) : (
                                    <button
                                        onClick={() => setConfirmDeleteId(it.id)}
                                        data-testid={`admin-feedback-delete-${it.id}`}
                                        aria-label="Delete this feedback"
                                        className="text-xs font-medium text-coral-dark hover:bg-coral-light/40 rounded-2xl px-2.5 py-1"
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                        <p className="text-[#1F2624] mt-3 leading-relaxed whitespace-pre-wrap">{it.message || <span className="italic text-[#6E7A74]">(no written message)</span>}</p>
                        {it.email && (
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <span className="text-xs uppercase tracking-wider font-semibold text-[#4C5551]">Reply to:</span>
                                <a href={`mailto:${it.email}?subject=Re: Your Memory Lane feedback`}
                                    data-testid={`admin-feedback-mailto-${it.id}`}
                                    className="inline-flex items-center gap-1.5 text-sm text-teal-dark hover:text-teal underline underline-offset-2">
                                    <Mail size={14} strokeWidth={1.8} /> {it.email}
                                </a>
                            </div>
                        )}
                    </article>
                ))}
            </section>
        </>
    );
}

